<!--
  ApplyToMoreProducts.vue -- Store Decorations pebble 3d: the bulk-apply
  "Apply to more products" WRITE surface on the open decoration.

  Card: https://trello.com/c/oSOHJfg0 (pebble 3d).
  Locked Decisions 17-20 / 17a-c.

  Role
  ====

  Extends ONE decoration's coverage across the products in THIS store
  that do not already carry it. Renders a LIST (not the matrix) --
  product / candidate zone / fit verdict pill / checkbox -- with a
  "Select all that fit" control (fit rows only; resize-needed rows are a
  separate counted opt-in, 17a), a coverage bar (placed-of-eligible, 20),
  and a Zone-taken (17b) Replace/Skip/Cancel flow that maps onto the
  apply service's ``conflict_resolutions`` (Replace removes the other
  decoration from THAT product only).

  Writes go through the single-decoration apply endpoint
  (``POST /api/decorations/<id>/apply_to_products/``), which routes into
  the SAME ``decoration_set_apply.apply_placements`` cascade the set path
  uses -- NO second write path.

  Data sources (no new backend beyond pebble 3d's endpoints):
    - GET  /api/stores/<store_id>/products_readiness/  (candidate pool)
    - POST /api/decorations/<id>/preview_apply_to_products/  (verdicts +
      zone-taken conflicts)
    - POST /api/decorations/<id>/apply_to_products/  (the write)

  data-testid selectors only.
-->
<template>
  <div class="atmp" data-testid="apply-to-more-products">
    <div class="atmp__head">
      <span class="atmp__title" data-testid="apply-title">Apply to more products</span>
    </div>

    <!-- Coverage: placed-of-eligible with a bar (Locked Decision 20). -->
    <div class="atmp__coverage" data-testid="apply-coverage">
      <div class="atmp__coverage-bar" data-testid="apply-coverage-bar">
        <div
          class="atmp__coverage-fill"
          :style="{ width: coveragePct + '%' }"
        />
      </div>
      <span class="atmp__coverage-label" data-testid="apply-coverage-label">
        On {{ placedCount }} of {{ eligibleCount }} products
      </span>
    </div>

    <div v-if="loading" class="atmp__loading" data-testid="apply-loading">
      Checking fit across this store...
    </div>

    <div
      v-else-if="loadError"
      class="atmp__error"
      data-testid="apply-error"
    >
      Could not check fit. <button type="button" data-testid="apply-retry" @click="load">Retry</button>
    </div>

    <template v-else>
      <div v-if="candidates.length === 0" class="atmp__empty" data-testid="apply-empty">
        Every product in this store already carries this decoration.
      </div>

      <template v-else>
        <!-- Bulk selectors. "Select all that fit" = fit rows only (17a). -->
        <div class="atmp__controls">
          <button
            type="button"
            class="atmp__ctl"
            data-testid="apply-select-all-fit"
            @click="selectAllThatFit"
          >
            Select all that fit ({{ fitCount }})
          </button>
          <button
            v-if="remapCount > 0"
            type="button"
            class="atmp__ctl atmp__ctl--warn"
            data-testid="apply-add-resize"
            @click="addAllResize"
          >
            Add {{ remapCount }} that need resizing
          </button>
        </div>

        <ul class="atmp__list" data-testid="apply-list">
          <li
            v-for="row in candidates"
            :key="row.campaign_product_id"
            class="atmp__row"
            :data-testid="`apply-row-${row.campaign_product_id}`"
          >
            <label class="atmp__row-check">
              <input
                type="checkbox"
                :data-testid="`apply-checkbox-${row.campaign_product_id}`"
                :disabled="!selectable(row)"
                :checked="isSelected(row.campaign_product_id)"
                @change="toggle(row.campaign_product_id, $event.target.checked)"
              />
            </label>
            <!-- Jake (2026-08-23): product names on this surface are
                 links to that product's editor. Plain text when the
                 operator lacks product.edit, since the route guard would
                 bounce them to /admin. -->
            <router-link
              v-if="canEditProduct"
              class="atmp__row-name atmp__row-name--link"
              :to="productEditorRoute(row.campaign_product_id)"
              :data-testid="`apply-name-link-${row.campaign_product_id}`"
            >
              {{ row.name }}
            </router-link>
            <span
              v-else
              class="atmp__row-name"
              :data-testid="`apply-name-${row.campaign_product_id}`"
            >
              {{ row.name }}
            </span>
            <span class="atmp__row-zone" :data-testid="`apply-zone-${row.campaign_product_id}`">
              {{ zoneLabel() }}
            </span>
            <!-- Fix 3: a candidate the preview omitted is "couldn't verify",
                 a distinct signal from a real "won't fit" drop pill. -->
            <span
              v-if="row.unverified"
              class="atmp__unverified"
              :data-testid="`apply-unverified-${row.campaign_product_id}`"
              title="We couldn't check fit for this product. It can't be applied until it's re-checked."
            >
              Couldn't verify
            </span>
            <placement-compatibility-indicator
              v-else
              :verdict="row.verdict"
              :reason="row.reason"
              :size_delta_pct="row.size_delta_pct"
            />
            <span
              v-if="row.zoneTaken"
              class="atmp__taken"
              :data-testid="`apply-zone-taken-${row.campaign_product_id}`"
              title="This zone already has another decoration on this product."
            >
              Zone taken
            </span>
          </li>
        </ul>

        <div class="atmp__footer">
          <span class="atmp__selcount" data-testid="apply-selected-count">
            {{ selectedIds.length }} selected<template v-if="selectedRemapCount > 0">
              ({{ selectedRemapCount }} will be scaled)</template>
          </span>
          <button
            type="button"
            class="atmp__apply"
            data-testid="apply-submit"
            :disabled="selectedIds.length === 0 || applying || !canWrite"
            @click="onApplyClicked"
          >
            Apply to {{ selectedIds.length }} product<template v-if="selectedIds.length !== 1">s</template>
          </button>
        </div>
      </template>
    </template>

    <!-- Zone-taken Replace/Skip/Cancel dialog (Locked Decision 17b). Opens
         when the selection includes a product whose candidate zone is
         occupied and the operator has not yet chosen how to resolve it. -->
    <div
      v-if="zoneTakenDialogOpen"
      class="atmp__backdrop"
      data-testid="apply-zone-taken-dialog"
      @click.self="cancelZoneTaken"
    >
      <div class="atmp__dialog">
        <h4 class="atmp__dialog-title">Zone already in use</h4>
        <p class="atmp__dialog-body" data-testid="apply-zone-taken-body">
          On <strong>{{ activeConflictName }}</strong>, the
          <strong>{{ activeConflictZone }}</strong> zone already carries another
          decoration. Replacing removes that decoration from THIS product only --
          it stays on every other product.
        </p>
        <div class="atmp__dialog-actions">
          <button
            type="button"
            class="atmp__dialog-btn atmp__dialog-btn--warn"
            data-testid="apply-zone-taken-replace"
            @click="resolveZoneTaken('replace')"
          >
            Replace
          </button>
          <button
            type="button"
            class="atmp__dialog-btn"
            data-testid="apply-zone-taken-skip"
            @click="resolveZoneTaken('skip')"
          >
            Skip this product
          </button>
          <button
            type="button"
            class="atmp__dialog-btn atmp__dialog-btn--ghost"
            data-testid="apply-zone-taken-cancel"
            @click="cancelZoneTaken"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="skippedBelowMinimum > 0"
      class="atmp__error"
      role="status"
      data-testid="apply-skipped-below-minimum"
    >
      {{ skippedBelowMinimum }} skipped: image too small for the minimum
    </div>

    <div
      v-if="applyError"
      class="atmp__error"
      data-testid="apply-apply-error"
    >
      {{ applyError }}
    </div>
  </div>
</template>

<script>
// Explicit import (not the router's global registration): several
// specs mount this component with no router installed, where an
// unresolved <router-link> renders nothing and only warns.
import { RouterLink } from "vue-router";
import api from "@/services/api";
import PlacementCompatibilityIndicator from "./PlacementCompatibilityIndicator.vue";

export default {
  name: "ApplyToMoreProducts",

  components: {
    "placement-compatibility-indicator": PlacementCompatibilityIndicator,
    PlacementCompatibilityIndicator,
    RouterLink,
    "router-link": RouterLink,
  },

  props: {
    storeId: { type: [Number, String], default: null },
    // The open rollup decoration (decoration_id + placements + method).
    decoration: { type: Object, required: true },
    canWrite: { type: Boolean, default: false },
  },

  emits: ["applied"],

  inject: {
    distributorLayoutCapabilities: { default: () => [] },
  },

  data() {
    return {
      loading: false,
      loadError: false,
      applying: false,
      applyError: "",
      // Card #573: placements the server skipped because the image is below
      // the method's minimum DPI (the apply result's
      // ``placements_skipped_below_minimum``).
      skippedBelowMinimum: 0,
      // All campaign_products in the store: [{ campaign_product_id, name }].
      storeProducts: [],
      // Preview verdict + conflict, keyed by campaign_product_id.
      previewByCp: {},
      // Selected candidate ids.
      selectedIds: [],
      // Per-product conflict resolutions the operator has answered, keyed
      // by String(cp_id) -> { conflict_resolutions: { <placementId>: v } }.
      resolutions: {},
      // Zone-taken dialog state.
      zoneTakenDialogOpen: false,
      // Queue of selected zone-taken cp ids still needing an answer.
      pendingConflictQueue: [],
      activeConflictCp: null,
    };
  },

  computed: {
    // Capabilities via the DistributorLayout injection (the pattern used
    // by ClientDepartmentsPanel, DecorationLibraryArtworkDetail and the
    // other distributor surfaces). Injection rather than the Pinia-backed
    // usePermissions composable because this component is mounted directly
    // by specs that install no Pinia, and `inject` has a safe [] default
    // where getActivePinia() throws.
    capabilities() {
      const inj = this.distributorLayoutCapabilities;
      if (Array.isArray(inj)) return inj;
      if (inj && typeof inj === "object" && Array.isArray(inj.value)) {
        return inj.value;
      }
      return [];
    },

    // `canWrite` (prop) governs APPLYING a decoration; opening the
    // product editor is a DIFFERENT capability and one does not imply
    // the other. The product_editor route is guarded by
    // requireCapability('product.edit', '/admin') (router/index.js), so a
    // caller without it is redirected OUT of the store editor rather than
    // landing on the editor. Without the capability the name stays the
    // plain text it was.
    canEditProduct() {
      return this.capabilities.includes("product.edit");
    },

    decorationId() {
      return this.decoration?.decoration_id ?? null;
    },

    // campaign_product_ids that already carry this decoration (from the
    // rollup's placements) -- excluded from the candidate list.
    placedCpIds() {
      const placements = Array.isArray(this.decoration?.placements)
        ? this.decoration.placements
        : [];
      return new Set(
        placements
          .map((p) => p.campaign_product_id)
          .filter((v) => v != null),
      );
    },

    // The zone the decoration currently occupies (source zone id) -- the
    // apply path prefers landing in a zone the decoration already uses
    // (Locked Decision 16b). Taken from the first placement with a zone.
    sourceZoneId() {
      const placements = Array.isArray(this.decoration?.placements)
        ? this.decoration.placements
        : [];
      for (const p of placements) {
        if (p.placement_zone && p.placement_zone.id != null) {
          return p.placement_zone.id;
        }
      }
      return null;
    },

    sourceZoneLabel() {
      const placements = Array.isArray(this.decoration?.placements)
        ? this.decoration.placements
        : [];
      for (const p of placements) {
        if (p.placement_zone) {
          return (
            p.placement_zone.display_name || p.placement_zone.slug || "zone"
          );
        }
      }
      return "zone";
    },

    // Candidate rows: store products NOT already carrying the decoration,
    // enriched with the preview verdict + zone-taken flag.
    //
    // A candidate absent from the preview response is flagged ``unverified``
    // (Fix 3): the endpoint is expected to echo every id it was sent, so a
    // missing id is a silent-failure path. Rather than default it to a
    // "blocked"/"won't fit" verdict (which would misrepresent it as a real
    // incompatibility and hide the gap), we mark it distinctly so the UI can
    // surface a "couldn't verify" signal and it stays out of both the fit
    // counts and the eligible denominator.
    candidates() {
      return this.storeProducts
        .filter((p) => !this.placedCpIds.has(p.campaign_product_id))
        .map((p) => {
          const pv = this.previewByCp[p.campaign_product_id];
          const unverified = pv === undefined;
          return {
            campaign_product_id: p.campaign_product_id,
            name: p.name,
            verdict: pv?.verdict ?? "blocked",
            reason: pv?.reason ?? "",
            size_delta_pct:
              pv?.size_delta_pct != null ? pv.size_delta_pct : null,
            zoneTaken: !!pv?.zoneTaken,
            occupantPlacementId: pv?.occupantPlacementId ?? null,
            unverified,
          };
        });
    },

    // Coverage denominators (Locked Decision 20; Fix 1, Jake 2026-08-05):
    // "eligible" = products this decoration CAN go on = placed products +
    // candidates that are actually applicable (verdict fit or remap). drop /
    // blocked (structurally incompatible, no compatible zone) and unverified
    // candidates are EXCLUDED -- the bar reads "placed of the products this
    // CAN go on", not "placed of every product in the store". The eligibility
    // predicate is the same one ``selectable()`` uses to enable a row.
    eligibleCount() {
      const eligibleCandidates = this.candidates.filter((r) =>
        this.selectable(r),
      ).length;
      return this.placedCount + eligibleCandidates;
    },
    placedCount() {
      // Count placed products that are actually in the store product list.
      return this.storeProducts.filter((p) =>
        this.placedCpIds.has(p.campaign_product_id),
      ).length;
    },
    coveragePct() {
      if (this.eligibleCount === 0) return 0;
      return Math.round((this.placedCount / this.eligibleCount) * 100);
    },

    fitCount() {
      return this.candidates.filter((r) => r.verdict === "fit").length;
    },
    remapCount() {
      return this.candidates.filter((r) => r.verdict === "remap").length;
    },
    selectedRemapCount() {
      const sel = new Set(this.selectedIds);
      return this.candidates.filter(
        (r) => sel.has(r.campaign_product_id) && r.verdict === "remap",
      ).length;
    },

    // Zone-taken dialog copy for the active conflict row.
    activeConflictName() {
      const row = this.candidates.find(
        (r) => r.campaign_product_id === this.activeConflictCp,
      );
      return row ? row.name : "";
    },
    activeConflictZone() {
      return this.sourceZoneLabel;
    },
  },

  watch: {
    decorationId() {
      this.reset();
      this.load();
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    // Named route, not a built path, so a future route-path change cannot
    // silently break these links. The param is a CAMPAIGN product id --
    // the same id the candidate rows are already keyed by.
    productEditorRoute(campaignProductId) {
      return {
        name: "product_editor",
        params: { campaignProductId },
      };
    },

    reset() {
      this.selectedIds = [];
      this.resolutions = {};
      this.previewByCp = {};
      this.zoneTakenDialogOpen = false;
      this.pendingConflictQueue = [];
      this.activeConflictCp = null;
      this.applyError = "";
    },

    async load() {
      if (this.storeId == null || this.decorationId == null) return;
      this.loading = true;
      this.loadError = false;
      try {
        const { data } = await api.get(
          `/stores/${this.storeId}/products_readiness/`,
        );
        this.storeProducts = Array.isArray(data?.products)
          ? data.products.map((p) => ({
              campaign_product_id: p.campaign_product_id,
              name: p.name,
            }))
          : [];
        await this.loadPreview();
      } catch (err) {
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },

    async loadPreview() {
      const candidateIds = this.storeProducts
        .filter((p) => !this.placedCpIds.has(p.campaign_product_id))
        .map((p) => p.campaign_product_id);
      if (candidateIds.length === 0) {
        this.previewByCp = {};
        return;
      }
      const { data } = await api.post(
        `/decorations/${this.decorationId}/preview_apply_to_products/`,
        {
          campaign_product_ids: candidateIds,
          source_zone_id: this.sourceZoneId,
          mode: "merge",
        },
      );
      const byCp = {};
      const rows = Array.isArray(data?.per_product) ? data.per_product : [];
      for (const row of rows) {
        const verdicts = Array.isArray(row.placement_verdicts)
          ? row.placement_verdicts
          : [];
        const v = verdicts[0] || {};
        const conflicts = Array.isArray(row.conflicts) ? row.conflicts : [];
        const occupant = conflicts.length > 0 ? conflicts[0] : null;
        byCp[row.campaign_product_id] = {
          verdict: v.verdict ?? "blocked",
          reason: v.reason ?? "",
          size_delta_pct:
            v.size_delta_pct != null ? v.size_delta_pct : null,
          zoneTaken: !!occupant,
          // The apply conflict-resolution contract keys on the existing
          // placement id (preview emits it as existing_decoration_id --
          // the field name is legacy; the value is the placement id).
          occupantPlacementId: occupant
            ? occupant.existing_decoration_id
            : null,
        };
      }
      this.previewByCp = byCp;

      // Fix 3: the preview endpoint is expected to echo a verdict for every
      // candidate id it was sent. Any id missing from the response is a
      // silent-failure path -- surface it (the row is flagged ``unverified``
      // in ``candidates`` and rendered with a distinct "couldn't verify"
      // badge; this warn makes the gap observable in logs / the console).
      const missing = candidateIds.filter((id) => !(id in byCp));
      if (missing.length > 0) {
        // eslint-disable-next-line no-console
        console.warn(
          `ApplyToMoreProducts: preview_apply_to_products returned no verdict for candidate campaign_product_ids ${missing.join(
            ", ",
          )} -- these will show as "couldn't verify" and cannot be applied.`,
        );
      }
    },

    // Fix 2: every candidate lands in the decoration's single source zone
    // (Locked Decision 16b: land in the zone the decoration already
    // occupies), so the label is not per-row today. The arg is dropped to
    // avoid signalling a per-row variation that does not exist -- if
    // per-product candidate zones are ever introduced, re-add the arg and
    // compute from it.
    zoneLabel() {
      return this.sourceZoneLabel;
    },

    // "drop" verdicts cannot be selected (Locked Decision 19). blocked /
    // N/A and unverified (Fix 3: preview-missing) rows are also
    // non-selectable -- we only enable rows we affirmatively verified as
    // fit or remap.
    selectable(row) {
      if (row.unverified) return false;
      return row.verdict === "fit" || row.verdict === "remap";
    },

    isSelected(cpId) {
      return this.selectedIds.includes(cpId);
    },

    toggle(cpId, checked) {
      const set = new Set(this.selectedIds);
      if (checked) set.add(cpId);
      else set.delete(cpId);
      this.selectedIds = Array.from(set);
    },

    // Select all FIT rows only (17a) -- remap is a separate opt-in.
    selectAllThatFit() {
      const set = new Set(this.selectedIds);
      for (const row of this.candidates) {
        if (row.verdict === "fit") set.add(row.campaign_product_id);
      }
      this.selectedIds = Array.from(set);
    },

    // Separate counted opt-in for the resize-needed rows (17a).
    addAllResize() {
      const set = new Set(this.selectedIds);
      for (const row of this.candidates) {
        if (row.verdict === "remap") set.add(row.campaign_product_id);
      }
      this.selectedIds = Array.from(set);
    },

    // ---- Apply flow -------------------------------------------------
    onApplyClicked() {
      // Enqueue any selected zone-taken product that has not yet been
      // resolved. Resolve them one dialog at a time (17b), then commit.
      this.applyError = "";
      const sel = new Set(this.selectedIds);
      const unresolved = this.candidates.filter(
        (r) =>
          sel.has(r.campaign_product_id) &&
          r.zoneTaken &&
          !this.resolutions[String(r.campaign_product_id)],
      );
      if (unresolved.length > 0) {
        this.pendingConflictQueue = unresolved.map(
          (r) => r.campaign_product_id,
        );
        this.openNextConflict();
        return;
      }
      this.commitApply();
    },

    openNextConflict() {
      if (this.pendingConflictQueue.length === 0) {
        this.zoneTakenDialogOpen = false;
        this.activeConflictCp = null;
        this.commitApply();
        return;
      }
      this.activeConflictCp = this.pendingConflictQueue[0];
      this.zoneTakenDialogOpen = true;
    },

    activeConflictRow() {
      return this.candidates.find(
        (r) => r.campaign_product_id === this.activeConflictCp,
      );
    },

    resolveZoneTaken(choice) {
      const cpId = this.activeConflictCp;
      const row = this.activeConflictRow();
      if (cpId == null || !row) {
        this.cancelZoneTaken();
        return;
      }
      if (choice === "skip") {
        // Drop the product from the selection entirely.
        this.selectedIds = this.selectedIds.filter((id) => id !== cpId);
      } else if (choice === "replace") {
        // Map onto the apply service conflict_resolutions: Replace removes
        // the occupant from THIS product only.
        const key = String(cpId);
        const existing = this.resolutions[key] || {};
        const cr = { ...(existing.conflict_resolutions || {}) };
        if (row.occupantPlacementId != null) {
          cr[String(row.occupantPlacementId)] = "replace";
        }
        this.resolutions = {
          ...this.resolutions,
          [key]: { ...existing, conflict_resolutions: cr },
        };
      }
      // Advance the queue.
      this.pendingConflictQueue = this.pendingConflictQueue.slice(1);
      this.openNextConflict();
    },

    cancelZoneTaken() {
      this.zoneTakenDialogOpen = false;
      this.pendingConflictQueue = [];
      this.activeConflictCp = null;
    },

    async commitApply() {
      if (this.selectedIds.length === 0) return;
      this.applying = true;
      this.applyError = "";
      this.skippedBelowMinimum = 0;
      try {
        const perProduct = {};
        for (const [key, val] of Object.entries(this.resolutions)) {
          if (this.selectedIds.includes(Number(key))) {
            perProduct[key] = val;
          }
        }
        const { data } = await api.post(
          `/decorations/${this.decorationId}/apply_to_products/`,
          {
            campaign_product_ids: [...this.selectedIds],
            source_zone_id: this.sourceZoneId,
            mode: "merge",
            per_product_resolutions: perProduct,
          },
        );
        this.skippedBelowMinimum =
          Number(data && data.placements_skipped_below_minimum) || 0;
        this.$emit("applied", data);
        // Clear the local selection; the parent refreshes the rollup.
        this.selectedIds = [];
        this.resolutions = {};
      } catch (err) {
        this.applyError =
          err?.response?.data?.error ||
          "Could not apply. Please re-check and retry.";
      } finally {
        this.applying = false;
      }
    },
  },
};
</script>

<style scoped>
.atmp {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #e5e7eb;
}
.atmp__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.atmp__title {
  font-weight: 600;
  font-size: 13px;
  color: #111827;
}
.atmp__coverage {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.atmp__coverage-bar {
  flex: 1 1 auto;
  height: 6px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
}
.atmp__coverage-fill {
  height: 100%;
  background: #10b981;
  transition: width 0.2s ease;
}
.atmp__coverage-label {
  flex: 0 0 auto;
  font-size: 12px;
  color: #4b5563;
}
.atmp__controls {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}
.atmp__ctl {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  cursor: pointer;
}
.atmp__ctl--warn {
  border-color: #fde68a;
  background: #fffbeb;
  color: #92400e;
}
.atmp__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.atmp__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 4px;
  border-radius: 6px;
}
.atmp__row:hover {
  background: #f9fafb;
}
.atmp__row-name {
  flex: 1 1 auto;
  font-size: 13px;
  color: #111827;
}
/* Linked form -- undecorated at rest so the row reads unchanged; the
   affordance appears on hover. */
.atmp__row-name--link {
  text-decoration: none;
  cursor: pointer;
}
.atmp__row-name--link:hover,
.atmp__row-name--link:focus-visible {
  color: #119ae9;
  text-decoration: underline;
}
.atmp__row-zone {
  flex: 0 0 auto;
  font-size: 12px;
  color: #6b7280;
}
.atmp__taken {
  flex: 0 0 auto;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}
/* Fix 3: "couldn't verify" -- neutral slate, deliberately NOT the red
   "won't fit" treatment so it reads as an unknown, not an incompatibility. */
.atmp__unverified {
  flex: 0 0 auto;
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  border: 1px solid #e5e7eb;
  font-weight: 500;
}
.atmp__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
}
.atmp__selcount {
  font-size: 12px;
  color: #4b5563;
}
.atmp__apply {
  font-size: 13px;
  padding: 6px 14px;
  border-radius: 6px;
  border: none;
  background: #2563eb;
  color: white;
  cursor: pointer;
}
.atmp__apply:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.atmp__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.atmp__dialog {
  background: white;
  border-radius: 10px;
  padding: 20px;
  max-width: 420px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}
.atmp__dialog-title {
  margin: 0 0 8px;
  font-size: 15px;
}
.atmp__dialog-body {
  margin: 0 0 16px;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
}
.atmp__dialog-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.atmp__dialog-btn {
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  cursor: pointer;
}
.atmp__dialog-btn--warn {
  border-color: #ef4444;
  background: #ef4444;
  color: white;
}
.atmp__dialog-btn--ghost {
  border-color: transparent;
  background: transparent;
  color: #6b7280;
}
.atmp__error {
  margin-top: 8px;
  font-size: 12px;
  color: #991b1b;
}
.atmp__loading,
.atmp__empty {
  font-size: 12px;
  color: #6b7280;
  padding: 8px 0;
}
</style>
